import { NetworkStatus } from '@apollo/client';
import { ACCESS_REQUESTS_PAGE_SIZE } from '@frameio/access-requests/constants/';
import { useGetAccessRequestsQuery } from '@frameio/access-requests/gql/queries/generated/GetAccessRequests';
import { useCursorPagination } from '@frameio/graphql-client';
import type { NotificationGroup } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useAccountVersion } from '@frameio/shared/contexts/ActiveAccountIdContext/useAccountVersion';
import {
  NOTIFICATIONS_LIST_FILTERS,
  type NotificationsListFilters,
} from '../components/NotificationsListFilter/types';
import { NON_NULL_ERROR, PAGE_SIZE } from '../constants';
import { useShouldShowTruncatedAccessRequestsList } from '../contexts/ShouldShowTruncatedAccessRequestsList';
import {
  useGetNotificationGroupsQuery,
  useGetUnopenedNotificationGroupsCountQuery,
} from '../gql';
import { usePreloadImages } from './usePreloadImages';
/**
 * @description The `useNotificationGroups` hook returns the results of `GetNotificationGroupsQuery` query. This is
 * the base query used to fetch NotificationGroups.
 *
 * @returns {Object}
 * @property {GetNotificationGroupsQueryResult} data the data returned by the `GetNotificationGroupsQuery` hook.
 * @property {Function} error the error returned by the `GetNotificationGroupsQuery` hook.
 * @property {Function} fetchNext the `fetchNext` function returned by the `GetNotificationGroupsQuery` hook.
 * @property {boolean} getUnopenedCountLoading whether the `GetUnopenedNotificationGroupsCountQuery` is loading.
 * @property {boolean} loading whether the `GetNotificationGroupsQuery` is loading.
 * @property {NetworkStatus} networkStatus the current networkStatus of the `GetNotificationGroupsQuery`.
 * @property {number} newNotificationsCount the number of new notifications for the current User.
 * @property {PageInfo} pageInfo the pageInfo object returned by the `GetNotificationGroupsQuery` hook.
 */
export function useNotificationGroups({
  filter = NOTIFICATIONS_LIST_FILTERS.ALL,
}: {
  filter?: NotificationsListFilters;
}) {
  const accountId = useActiveAccountId();
  const accountVersion = useAccountVersion();
  const skip = !accountId || !accountVersion || accountVersion < 4;

  const unreadOnly =
    filter === NOTIFICATIONS_LIST_FILTERS.UNREAD ? { unreadOnly: true } : {};

  const { shouldShowTruncatedAccessRequestsList } =
    useShouldShowTruncatedAccessRequestsList();

  const { data: payload, loading: getUnopenedCountLoading } =
    useGetUnopenedNotificationGroupsCountQuery({
      variables: { accountId },
      fetchPolicy: 'cache-only',
      nextFetchPolicy: 'cache-only',
      skip,
    });
  const newNotificationsCount =
    payload?.account?.notificationGroups?.totalCount || 0;

  /**
   * Render access requests at the top of the notification list, using
   * the nodes from the access requests table for easier grouping and
   * to prevent the display of stale data from the notifications payload.
   *
   * To do this, we make 2 separate queries:
   *
   * 1. Fetch the nodes from the access requests table to display them
   *    first. Then only after all access requests have been retrieved:
   * 2. Fetch the nodes from the notifications table with the type
   *    `NotificationGroupAccessRequestSubmitted` excluded.
   *
   * Note that actioned access requests (denied or approved) are
   * included as part of the regular notification payload.
   */

  /** First retrieve the paginated pending access request.
   */
  const {
    data: accessRequestsData,
    loading: accessRequestsLoading,
    fetchNext: fetchNextAccessRequests,
    networkStatus: accessRequestsNetworkStatus,
    error: accessRequestsError,
  } = useCursorPagination(
    useGetAccessRequestsQuery({
      variables: {
        accountId,
        page: { first: ACCESS_REQUESTS_PAGE_SIZE },
      },
      errorPolicy: 'all',
      skip: skip || filter === NOTIFICATIONS_LIST_FILTERS.UNREAD,
    })
  );

  const hasAccessRequests =
    (accessRequestsData?.account?.accessRequests?.totalCount &&
      accessRequestsData?.account?.accessRequests?.totalCount > 0) ||
    false;
  let hasMoreAccessRequests =
    accessRequestsData?.account?.accessRequests?.pageInfo?.hasNextPage ?? false;

  if (shouldShowTruncatedAccessRequestsList) {
    hasMoreAccessRequests = false;
  }

  /** When there are no more access requests, retrieve the other notifications.
   */
  const {
    data: notificationGroupsData,
    error: notificationGroupsError,
    fetchNext: fetchNextNotificationGroups,
    networkStatus: notificationGroupsNetworkStatus,
    loading: notificationGroupsLoading,
  } = useCursorPagination(
    useGetNotificationGroupsQuery({
      variables: {
        accountId,
        page: { first: PAGE_SIZE },
        ...unreadOnly,
      },
      errorPolicy: 'all',
      skip: skip || hasMoreAccessRequests,
    })
  );

  /** show full height loading indicator only once, upon first loading
   * either access requests or notification groups
   */
  const loading = hasAccessRequests
    ? accessRequestsLoading
    : notificationGroupsLoading;

  const fetchNext = hasMoreAccessRequests
    ? fetchNextAccessRequests
    : fetchNextNotificationGroups;

  const pageSize = hasMoreAccessRequests
    ? ACCESS_REQUESTS_PAGE_SIZE
    : PAGE_SIZE;

  const operationError = accessRequestsError || notificationGroupsError;

  /**
   * A skipped query still reports `NetworkStatus.ready`, so the two statuses
   * can't be combined with `||` without masking the other query's states.
   * Report the active query's status, but let `fetchMore` from either query
   * win so consumers keep the list mounted (and its scroll position) while a
   * page loads, including across the access requests → notification groups
   * handoff.
   */
  const activeNetworkStatus = hasMoreAccessRequests
    ? accessRequestsNetworkStatus
    : notificationGroupsNetworkStatus;
  const networkStatus =
    accessRequestsNetworkStatus === NetworkStatus.fetchMore ||
    notificationGroupsNetworkStatus === NetworkStatus.fetchMore
      ? NetworkStatus.fetchMore
      : activeNetworkStatus;

  /**
   * We would like to partially render the `NotificationsList` even if there are
   * `null` `NotificationGroups`.
   *
   * If there is more than one `graphQLError` in the `graphQLErrors` array, we
   * want to check if they are all the same error, otherwise we render the error
   * component.
   *
   * If there's a `networkError`, we want to render the error component.
   */
  const isNonNullError =
    (operationError?.graphQLErrors?.every(
      (graphQLError) => graphQLError?.message === NON_NULL_ERROR
    ) &&
      !operationError?.networkError) ??
    false;

  /**
   * Since we are attempting to render partial data, we need to make sure that
   * we don't render an error fallback.
   */
  const error = isNonNullError ? undefined : operationError;

  const pageInfo = hasMoreAccessRequests
    ? accessRequestsData?.account?.accessRequests?.pageInfo
    : notificationGroupsData?.account?.notificationGroups?.pageInfo;

  const mergedData = {
    account: {
      ...accessRequestsData?.account,
      ...notificationGroupsData?.account,
    },
  };

  usePreloadImages({
    notifications: mergedData?.account?.notificationGroups
      ?.nodes as NotificationGroup[],
  });

  return {
    data: mergedData,
    error,
    fetchNext,
    getUnopenedCountLoading,
    loading,
    networkStatus,
    newNotificationsCount,
    pageInfo,
    pageSize,
  };
}
